import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Chip,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  addToast,
} from "@heroui/react";
import { GetSubscriptions } from "@/server-types";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { MoreHorizontal, Users } from "lucide-react";
import { getLocalizedName, getLocalizedValue } from "../../../../utils";
import { api } from "@/lib/api-client";
import { useNavigate } from "react-router";

type Subscription = GetSubscriptions["response"][number];

export const SubscriptionCard = ({
  subscription,
  onEdit,
  refetchSubscriptions,
}: {
  subscription: Subscription;
  onEdit: (subscription: Subscription) => void;
  refetchSubscriptions: () => void;
}) => {
  const { mutateAsync: deleteSubscription } =
    api.subscription.useDeleteSubscription();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onOpenDeleteModal,
    onOpenChange: onOpenChangeDeleteModal,
  } = useDisclosure();

  const {
    isOpen: isDeactivateModalOpen,
    onOpen: onOpenDeactivateModal,
    onOpenChange: onOpenChangeDeactivateModal,
  } = useDisclosure();

  const { mutateAsync: updateSubscription } =
    api.subscription.usePutSubscription();

  function handleDeleteModalOpenChange() {
    onOpenChangeDeleteModal();
  }

  function handleDeactivateModalOpenChange() {
    onOpenChangeDeactivateModal();
  }

  async function handleDelete() {
    try {
      await deleteSubscription(subscription.id);
      addToast({
        title: t`Uspešno`,
        description: t`Naročnina je bila uspešno izbrisana`,
        color: "success",
      });
      handleDeleteModalOpenChange();
      refetchSubscriptions();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri brisanju naročnine`,
        color: "danger",
      });
    }
  }

  async function handleDeactivate() {
    try {
      await updateSubscription({
        subscriptionId: subscription.id,
        body: {
          ...subscription,
          isActive: false,
          tagIds: subscription.tags.map((tag) => tag.id),
        },
      });
      addToast({
        title: t`Uspešno`,
        description: t`Naročnina je bila uspešno deaktivirana`,
        color: "success",
      });
      handleDeactivateModalOpenChange();
      refetchSubscriptions();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri deaktiviranju naročnine`,
        color: "danger",
      });
    }
  }

  const navigate = useNavigate();

  return (
    <>
      <Card
        isPressable
        isHoverable
        radius="sm"
        shadow="none"
        className="h-full min-h-64 w-full"
        classNames={{
          base: "border-1 border-gray-200",
        }}
        onPress={() =>
          navigate(`/dashboard/subscriptions/${subscription.id}/variations`)
        }
      >
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">{subscription.subscribersCount}</span>
            </div>
            {!subscription.isActive && (
              <Chip color="danger" size="sm" variant="flat">
                <Trans>Neaktivno</Trans>
              </Chip>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <h3 className="flex items-center gap-2 text-xl font-medium">
            <span
              className="inline-block h-4 w-4 rounded-sm"
              style={{ backgroundColor: subscription.color }}
            />
            {getLocalizedValue(subscription.nameLocalized)}
          </h3>
          <p className="text-xs">
            <Trans>Število variacij:</Trans>{" "}
            {subscription.subscriptionVariations.length}
          </p>
          <p className="text-xs">
            <Trans>Število storitev:</Trans>{" "}
            {subscription.subscriptionServices.length}
          </p>

          <div className="mt-1">
            {subscription.subscriptionServices.map((service) => (
              <p key={service.serviceId} className="text-xs font-medium">
                • {getLocalizedName(service.service.nameLocalized)}
              </p>
            ))}
          </div>
        </CardBody>
        <CardFooter className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {subscription.tags.map((tag) => (
              <Chip key={tag.id} size="sm" radius="sm">
                {getLocalizedValue(tag.nameLocalized)}
              </Chip>
            ))}
          </div>

          <Dropdown shouldBlockScroll={false}>
            <DropdownTrigger>
              <Button
                isIconOnly
                variant="light"
                size="sm"
                data-identifier="subscription-card-actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Subscription actions"
              onAction={(key) => {
                if (key === "edit") {
                  onEdit(subscription);
                } else if (key === "delete") {
                  onOpenDeleteModal();
                } else if (key === "deactivate") {
                  onOpenDeactivateModal();
                }
              }}
            >
              <DropdownItem
                key="edit"
                data-identifier="edit-subscription-button"
              >
                <Trans>Uredi</Trans>
              </DropdownItem>
              <DropdownItem
                key="delete"
                className="text-danger"
                color="danger"
                data-identifier="delete-subscription-button"
              >
                <Trans>Izbriši</Trans>
              </DropdownItem>
              <DropdownItem key="deactivate">
                <Trans>Deaktiviraj</Trans>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </CardFooter>
      </Card>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalOpenChange}
        onOpenChange={handleDeleteModalOpenChange}
      >
        <ModalContent>
          <ModalHeader>{t`Izbriši naročnino`}</ModalHeader>
          <ModalBody>
            <p>{t`Ali ste prepričani, da želite izbrisati to naročnino?`}</p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={handleDeleteModalOpenChange}
            >{t`Prekliči`}</Button>
            <Button
              color="danger"
              data-identifier="confirm-delete-subscription-button"
              onPress={handleDelete}
            >{t`Izbriši`}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isDeactivateModalOpen}
        onClose={handleDeactivateModalOpenChange}
        onOpenChange={handleDeactivateModalOpenChange}
      >
        <ModalContent>
          <ModalHeader>{t`Deaktiviraj naročnino`}</ModalHeader>
          <ModalBody>
            <p>{t`Ali ste prepričani, da želite deaktivirati to naročnino?`}</p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={handleDeactivateModalOpenChange}
            >{t`Prekliči`}</Button>
            <Button
              color="warning"
              onPress={handleDeactivate}
            >{t`Deaktiviraj`}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
